/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import constants from '../constants';
import { resolveRestApi } from '../router/app-router';
import { fetch as axiosFetch } from '../service_broker/fetch';
import { isAssignmentInCreateStage, isLocalAction } from '../utils/case-utils';
import { getContainerItemData } from '../container/container-utils';
import { GET_CASE_STAGES } from '../store/state/actions/index';
import { getParentContextName } from '../utils/context-utils';
import { getDataObject } from '../store/state/state-manager';
import { C11nEnv } from '../interpreter/c11n-env';

import type { ActionButtons, CaseAction, ChildCaseAssignment, CaseInfo as ImportedCaseInfo } from './types';

/**
 * Class to provide case information related API's
 */
class CaseInfo {
  readonly c11nEnv: C11nEnv;

  constructor(c11nEnv: C11nEnv) {
    this.c11nEnv = c11nEnv;
  }

  /**
   * Obtains the details of the case stages for a specified case.
   *
   ** @example <caption>Example for getCaseStages</caption>
   * In this example, the API returns the details of the case stages for the case with ID "OPB1HW-SPACETRA-WORK RA-43001" rendered in the "app/primary_1" context.
   * getPConnect()
   *  .getCaseInfo()
   *  .getCaseStages('OPB1HW-SPACETRA-WORK RA-43001', 'app/primary_1')
   *  .then((stages) => {
   *    // success handling
   *    console.log(stages);
   *  })
   *  .catch((error) => {
   *    // failure handling
   *  });
   *
   * @param {string} caseID - The unique identifier of the case whose case stage details must be fetched.
   * @param {string} contextName - The name of the context where the details of the case stages for a specified case are rendered.
   * @returns {Promise<object>} A promise that resolves to an object containing the case stages.
   */
  async getCaseStages(caseID: string, contextName: string): Promise<object> {
    const reqPayload = {
      caseID
    };
    const { restApi, reqType } = resolveRestApi(GET_CASE_STAGES, reqPayload);
    const response = await axiosFetch(
      restApi,
      {
        method: reqType
      },
      contextName
    );
    return response.data.stages;
  }

  /**
   * Obtains the name of the case type.
   *
   * @example <caption>In this example, the API returns the name of the case type.</caption>
   * const caseTypeName = getPConnect().getCaseInfo().getCaseTypeName();
   *
   * @returns Case type name
   * <br><br>
   * Eg: Registration
   */
  getCaseTypeName(): string {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_TYPE_NAME));
  }

  /**
   * Returns Case name
   *
   * @example <caption>Example for getName()</caption>
   * const caseName = getPConnect().getCaseInfo().getName();
   *
   * @returns Case name
   * <br><br>
   * Eg: Registration
   */
  getName(): string {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO_NAME));
  }

  /**
   * Returns Case class name
   *
   * @example <caption>Example for getClassName()</caption>
   * const className = getPConnect().getCaseInfo().getClassName();
   *
   * @returns Case class name
   * <br><br>
   * Eg: ON8TTL-C11nGall-Work-CreateStageTest
   */
  getClassName(): string {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_TYPE_ID));
  }

  /**
   * Fetch available processes for a case
   * @example <caption>Example for getAvailableProcesses()".</caption>
   * const availableProcesses = getPConnect().getCaseInfo().getAvailableProcesses();
   * @returns - returns available processes
   * <br>
   * Eg:  [
   *          {
   *              "name": "Vendor Addition",
   *              "links": {
   *              "add": {
   *                "rel": "self",
   *                "href": "/cases/ON8TTL-GALLERY-WORK PC-99001/processes/VendorAddition_Flow",
   *                "type": "POST",
   *                "title": "add case optional process"
   *                    }
   *                  },
   *               "ID": "VendorAddition_Flow",
   *               "type": "Case"
   *           }
   *        ]
   */
  getAvailableProcesses(): any[] {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.AVAILABLEPROCESSES));
  }

  /**
   * Returns Case key
   *
   * @example <caption>Example for getKey()</caption>
   * const caseKey = getPConnect().getCaseInfo().getKey();
   *
   * @returns Case key
   * <br><br>
   * Eg: ON8TTL-GALLERY-WORK CST-1
   */
  getKey(): string {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO_ID));
  }

  /**
   * Returns Object key
   *
   * @example <caption>Example for getObjectKey()</caption>
   * const caseKey = getPConnect().getCaseInfo().getObjectKey();
   *
   * @returns Object key
   * <br><br>
   * Eg: STE6QXV0aG9yaW5nQWxsWFuY2VBcHBfX1BancRfNjcyMkdMjFjMTExMDQxMGZmMTM4MmNl
   */
  getObjectKey(): string {
    return this.c11nEnv.getValue(constants.OBJECT_INFO.OBJECT_INFO_ID);
  }

  /**
   * Returns Case ID
   *
   * @example <caption>Example for getID()</caption>
   * const caseKey = getPConnect().getCaseInfo().getID();
   *
   * @returns - Case ID
   * <br><br>
   * Eg: CST-1
   */
  getID() {
    const caseKey = this.getKey();
    if (caseKey) {
      const [, ID] = caseKey.split(' ');
      return ID || caseKey;
    }
    return undefined;
  }

  /**
   * Returns Assignment ID
   *
   * @example <caption>Example for getAssignmentID()</caption>
   * const assignmentID = getPConnect().getCaseInfo().getAssignmentID();
   *
   * @returns Assignment ID
   * <br><br>
   * Eg: ASSIGN-WORKLIST ON8TTL-GALLERY-WORK CST-51001!CREATEFORM_DEFAULT
   */
  getAssignmentID(): string {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ASSIGNMENT_ID));
  }

  /**
   * Returns Case actions info
   *
   * @example <caption>Example for getActions()</caption>
   * const actions = getPConnect().getCaseInfo().getActions();
   *
   *
   * @returns Case actions info
   * <br>
   * Eg: {
   *       "secondary": [
   *           {
   *              "jsAction": "cancelAssignment",
   *              "name": "Cancel",
   *               "actionID": "cancel"
   *           }
   *       ],
   *       "main": [
   *           {
   *               "jsAction": "finishAssignment",
   *               "name": "Next   ",
   *               "actionID": "next"
   *           }
   *       ]
   *   }
   */
  getActions(): ActionButtons {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ACTION_BUTTONS));
  }

  /**
   * Returns current flow action ID that is rendered.
   *
   * @example <caption>Example for getActiveFlowActionID()</caption>
   * const activeFlowActionID = getPConnect().getCaseInfo().getActiveFlowActionID();
   *
   * @returns Current flow action ID
   * <br><br>
   * Eg: MyFlowAction1
   */
  getActiveFlowActionID(): string {
    const actionID = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ACTIVE_ACTION_ID));

    return actionID || this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ASSIGNMENTACTION_ID));
  }

  /**
   * Returns parent case info when the current assignment is in child case.
   *
   * @example <caption>Example for getParentCaseInfo()</caption>
   * const parentCaseInfo = getPConnect().getCaseInfo().getParentCaseInfo();
   *
   * @returns Parent Case details of current child Case.
   * <br><br>
   * Eg:
   *  {
   *       "hasMoreAncestors":"false",
   *       "name":"Parent case",
   *       "links":{
   *          "open":{
   *             "rel":"other",
   *             "href":"/cases/APP-FOOD-WORK P-13002",
   *             "type":"GET",
   *             "title":"open parent case"
   *          }
   *       },
   *       "ID":"APP-FOOD-WORK P-13002",
   *       "content":{
   *          "pyStatusWork":"New",
   *          "classID":"APP-Food-Work-ParentCase",
   *          "pxUpdatedOperatorAndDateTime":"Gallery less than a minute ago",
   *          "pxUrgencyWork":"10",
   *          "pxCreateOperatorAndDateTime":"Gallery 38 minutes ago"
   *       }
   *    }
   */
  getParentCaseInfo() {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.PARENTCASEINFO));
  }

  /**
   * Returns true if the view is in perform mode else false
   *
   * @example <caption>Example for isPerform()</caption>
   * const isPerform = getPConnect().getCaseInfo().isPerform();
   *
   * @returns Flag to indicate perform mode or not
   *
   */
  isPerform() {
    return (
      this.c11nEnv.getValue(`${constants.CONTEXT_DATA}${constants.CASE_VIEW_MODE_PROP}`) === constants.PERFORM_MODE
    );
  }

  /**
   * Returns true if the view is in review mode else false
   *
   * @example <caption>Example for isReview()</caption>
   * const isReview = getPConnect().getCaseInfo().isReview();
   *
   * @returns Flag to indicate review mode or not
   *
   */
  isReview() {
    return this.c11nEnv.getValue(`${constants.CONTEXT_DATA}${constants.CASE_VIEW_MODE_PROP}`) === constants.REVIEW_MODE;
  }

  /**
   * Checks and returns true if the current assignment is in create stage.
   * @private
   * @returns - Returns true if the current assignment is in create stage else false
   */
  isAssignmentInCreateStage() {
    const caseInfo = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO));
    return isAssignmentInCreateStage(caseInfo);
  }

  /**
   * Checks and returns true if the current action is local action.
   * @private
   * @returns - Returns true if the current action is local action else false
   */
  isLocalAction() {
    const caseInfo = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO));
    return isLocalAction(caseInfo);
  }

  /**
   * Obtains the name of the current assignment's view in a case.
   *
   * @example <caption>Example for getCurrentAssignmentViewName()</caption>
   * const viewName = getPConnect().getCaseInfo().getCurrentAssignmentViewName();
   *
   * @returns View name
   * <br><br>
   * Eg: EmployeeDetails
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  getCurrentAssignmentViewName(): string | null {
    const caseViewMode = this.c11nEnv.getValue('context_data.caseViewMode');
    let assignmentViewName: string | null = null;
    let assignmentIsInCreateStage = false;

    if (caseViewMode === constants.PERFORM_MODE) {
      if (this.isLocalAction()) {
        return assignmentViewName;
      }

      if (this.isAssignmentInCreateStage()) {
        assignmentIsInCreateStage = true;
      } else {
        // When cases with no create stage is launched in a modal, below ternary operator else block supports to return the view name.
        const target = this.c11nEnv.getTarget();
        const contextName = this.c11nEnv.getContextName();
        const containerItemData = getContainerItemData(target, contextName);
        const { config } =
          target && contextName ? containerItemData && containerItemData.view : this.c11nEnv.getMetadata();
        assignmentViewName = config?.type === constants.VIEW ? config?.name : null;
      }

      if (
        (assignmentViewName && assignmentViewName.startsWith(constants.PROPERTY_ANNOTATION)) ||
        assignmentIsInCreateStage
      ) {
        assignmentViewName = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.VIEW_NAME));
      }
    }
    return assignmentViewName;
  }

  /**
   * Retrieves a value from a nested object structure based on a given path.
   *
   * @param data - The object containing the case information.
   * @param path - The path to the desired value, using dot notation or array-like syntax.
   *               Example: "caseInfo.content.MyDataList(3)" or "caseInfo.content.property".
   * @returns The value at the specified path, or undefined if the path is invalid or does not exist.
   *
   * @example
   * const data = { caseInfo: { content: { MyDataList: [1, 2, 3] } } };
   * const value = getValueByPath(data, "caseInfo.content.MyDataList(2)");
   * Output: 2
   *
   * @private
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  getValueByPath(data: { caseInfo: ImportedCaseInfo }, path: string): unknown {
    // Remove "@P " prefix if present
    path = path.replace('@P ', '').trim();
    let current: unknown = data;

    // If the path is referring to pyViewContext
    if (path.includes('pyViewContext')) {
      const caseInfo = (data.caseInfo?.content ?? {}) as Record<string, any>;
      const pyViewContext = caseInfo.pyViewContext;
      if (pyViewContext === '') {
        return (current as { caseInfo: ImportedCaseInfo }).caseInfo.content;
      }
      if (typeof pyViewContext === 'string' && pyViewContext.includes('(') && pyViewContext.includes(')')) {
        const arrayKey = pyViewContext.substring(1, pyViewContext.indexOf('('));
        const index =
          parseInt(pyViewContext.substring(pyViewContext.indexOf('(') + 1, pyViewContext.indexOf(')')), 10) - 1;

        if (Array.isArray(caseInfo[arrayKey])) {
          return caseInfo[arrayKey][index];
        }
      }
    }

    // Split the path by "." and process each part
    const keys = path.split(/[.[\]]/).filter((key) => key);

    for (const key of keys) {
      if (typeof current === 'object' && current !== null && key in current) {
        current = (current as Record<string, unknown>)[key];
      } else if (Array.isArray(current) && !Number.isNaN(Number(key))) {
        current = current[parseInt(key, 10) - 1]; // Adjust index for 1-based indexing
      } else {
        return;
      }
    }

    return current;
  }

  /**
   * Obtains the name of the current assignment's view class.
   *
   * @example <caption>Example for getCurrentAssignmentViewClassName()</caption>
   * const viewName = getPConnect().getCaseInfo().getCurrentAssignmentViewClassName();
   *
   * @returns the name of the current assignment's view class as a string.
   * <br><br>
   * Eg: OYLGY0-MySandBox-Data-MyDataTypeA
   */
  getCurrentAssignmentViewClassName(): string | null {
    const target = this.c11nEnv.getTarget();
    const contextName = this.c11nEnv.getContextName();
    const containerItemData = getContainerItemData(target, contextName);
    const { config } = target && contextName && containerItemData ? containerItemData.view : this.c11nEnv.getMetadata();
    const caseInfo = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO));
    const assignmentViewContext = config?.type === constants.VIEW ? config.context : null;
    if (assignmentViewContext) {
      const result = this.getValueByPath({ caseInfo }, assignmentViewContext);
      if (typeof result === 'object' && result !== null && 'classID' in result) {
        return (result as { classID: string }).classID;
      }
    }
    return null;
  }

  /**
   * Returns the refresh condition of current active case action
   *
   * @example <caption>Example for getActionRefreshConditions()</caption>
   * const refreshConditions = getPConnect().getCaseInfo().getActionRefreshConditions();
   *
   * @returns Refresh Conditions
   * <br><br>
   * Eg:  refreshConditions: [{field: ".Datetime",event: "Changes"}, {field: ".Checkbox",event: "Changes"}]
   * @private
   */
  getActionRefreshConditions() {
    const isMultiRecordData = this.c11nEnv.getValue(
      constants.getProperty(constants.CONTEXT_DATA_MODAL_VIEW.IS_MULTI_RECORD_DATA)
    );

    if (isMultiRecordData) {
      return this.c11nEnv.getValue(constants.getProperty(constants.EMBEDDED_DATA_MODAL.REFRESH_CONDITIONS));
    }

    const activeActionID = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ACTIVE_ACTION_ID));
    const parentContext = getParentContextName(this.c11nEnv.getContextName());
    let parentContainerData;

    if (parentContext) {
      parentContainerData = getDataObject(parentContext);
    }
    const caseActions = parentContainerData?.caseInfo?.availableActions;
    const currentContainerData = getDataObject(this.c11nEnv.getContextName());
    const refreshConditionsForCurrentContext = currentContainerData?.caseInfo?.refreshConditions;

    if (activeActionID) {
      if (caseActions) {
        const actionObj = caseActions.find((caseAction: { ID: string }) => caseAction.ID === activeActionID);
        if (actionObj) {
          return actionObj.refreshConditions;
        }
        return refreshConditionsForCurrentContext;
      }
      return refreshConditionsForCurrentContext;
    }
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ACTION_REFRESH_CONDITIONS));
  }

  /**
   * Returns Business ID
   * @example <caption>Example for getBusinessID()</caption>
   * const caseKey = getPConnect().getCaseInfo().getBusinessID();
   *
   * @returns Business ID
   * <br><br>
   * Eg: CST-1
   */
  getBusinessID(): string {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.BUSINESS_ID));
  }

  getAssignments() {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO))?.assignments;
  }

  getChildAssignments() {
    return this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO))?.childCases?.[0]?.assignments;
  }

  isCaseWideLocalAction = () => {
    const actionID = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ACTIVE_ACTION_ID));
    const caseActions = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO_ACTIONS));

    if (caseActions && actionID) {
      const activeAction = caseActions.find((caseAction: CaseAction) => caseAction.ID === actionID);
      return activeAction?.type === 'Case';
    }
    return false;
  };

  getAllAssignments() {
    const caseActions = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO_ACTIONS));
    const assignmentLabel = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ASSIGNMENT_LABEL));
    const assignments =
      this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.D_CASE_ASSIGNMENTS_RESULTS)) || [];
    const childCasesAssignments = this.getChildAssignments() || [];
    let childCasesAssignmentsCopy = JSON.parse(JSON.stringify(childCasesAssignments));
    childCasesAssignmentsCopy = childCasesAssignmentsCopy.map((assignment: ChildCaseAssignment) => {
      assignment.isChild = true;
      return assignment;
    });

    const todoAssignments = [...assignments, ...childCasesAssignmentsCopy];
    let todoAssignmentsCopy = JSON.parse(JSON.stringify(todoAssignments));

    if (caseActions && !this.isPerform()) {
      todoAssignmentsCopy = todoAssignmentsCopy.map((assignment: ChildCaseAssignment) => {
        assignment.name = this.getActiveCaseActionName() || assignment?.name || assignmentLabel;
        return assignment;
      });
    }

    return todoAssignmentsCopy;
  }

  getActiveCaseActionName() {
    const caseActions = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO_ACTIONS));
    const activeActionID = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.ACTIVE_ACTION_ID));
    const activeAction = caseActions.find((action: CaseAction) => action.ID === activeActionID);
    return activeAction?.name || '';
  }

  getFirstCaseActionName() {
    const caseActions = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.CASE_INFO_ACTIONS));
    return caseActions[0]?.name || '';
  }

  hasAssignments() {
    const assignments = this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.D_CASE_ASSIGNMENTS_RESULTS));
    const childCasesAssignments = this.getChildAssignments();

    return !!(assignments || childCasesAssignments || this.isCaseWideLocalAction());
  }

  hasMessages = () => {
    return !!this.c11nEnv.getValue('caseMessages');
  };

  hasGetNextWorkLinks() {
    return !!this.c11nEnv.getValue(constants.getProperty(constants.CASE_INFO.GETNEXTWORK));
  }

  /**
   * Checks and returns true if the current action is object wide action.
   * @private
   * @returns - Returns true if the current action is object wide action else false
   */
  isObjectWideAction(): boolean {
    const objectInfo = this.c11nEnv.getValue(constants.OBJECT_INFO.OBJECT_INFO);
    return isLocalAction(objectInfo);
  }
}

export default CaseInfo;
